import React, { useState } from "react";
import { motion } from "framer-motion";
import SectionHeader from "./SectionHeader";
import { aiProducts } from "../mock";
import { ArrowUpRight, Mail } from "lucide-react";
import WaitlistModal from "./WaitlistModal";

const STATUS_STYLES = {
  Live: { bg: "#0F766E", text: "#fff", label: "Live" },
  Beta: { bg: "#1F2937", text: "#fff", label: "Beta" },
  Alpha: { bg: "#fff", text: "#1F2937", label: "Alpha", border: "#1F2937" },
  Preview: { bg: "#111827", text: "#FBBF24", label: "Preview" },
  Concept: { bg: "#fff", text: "#6B7280", label: "Concept", border: "#D1D5DB" },
};

const ProductsSection = () => {
  const [activeConfig, setActiveConfig] = useState(null);
  const openSignup = (product) => {
    if (!product.waitlist) return;
    setActiveConfig({
      ...product.waitlist,
      code: product.code,
      accent: product.accent,
      eyebrow: product.status === "Preview" ? "Early Access" : "Waitlist",
    });
  };
  return (
    <section
      id="products"
      className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#FAEDE7] relative overflow-hidden"
      data-testid="products-section"
    >
      {/* dotted texture */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #000 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-12 sm:mb-16">
          <SectionHeader
            eyebrow="AI Products"
            title="Five AI engines, built in-house"
            titleAccent="around our own foundation model."
            subtitle="A vertically integrated suite of research-grade AI tools — anchored by Kainos AI, our own foundation language model, and extending into grant writing, talent screening, literature discovery and peer review."
            index={2}
            className="mb-0"
          />

          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 flex-shrink-0">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>1 foundation · 5 specialized agents</span>
          </div>
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {aiProducts.map((p, i) => {
            const status = STATUS_STYLES[p.status] || STATUS_STYLES.Concept;
            const isSignup = p.cta === "signup";
            return (
              <motion.article
                key={p.id}
                data-testid={`product-card-${p.code.toLowerCase()}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.55,
                  delay: i * 0.07,
                  ease: [0.22, 1, 0.36, 1],
                }}
                onClick={isSignup ? () => openSignup(p) : undefined}
                className={`group relative bg-white border border-gray-200 rounded-lg p-6 sm:p-7 hover:border-gray-900 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.18)] flex flex-col ${
                  isSignup ? "cursor-pointer" : "cursor-default"
                } ${p.code === "KAI" ? "ring-1 ring-gray-900/10" : ""}`}
              >
                {/* Faint giant index */}
                <span
                  aria-hidden
                  className="absolute right-5 top-5 text-[64px] font-light leading-none text-gray-100 select-none tracking-tighter"
                >
                  {String(i + 1).padStart(2, "0")}
                </span>

                {/* Code + status */}
                <div className="flex items-center justify-between mb-6 relative z-10">
                  <span
                    className="px-2 py-0.5 text-[10px] font-mono tracking-[0.25em] rounded-sm text-white"
                    style={{ backgroundColor: p.accent }}
                  >
                    {p.code}
                  </span>
                  <span
                    className="px-2 py-0.5 text-[9px] font-mono uppercase tracking-[0.2em] rounded-full"
                    style={{
                      backgroundColor: status.bg,
                      color: status.text,
                      border: status.border ? `1px solid ${status.border}` : "none",
                    }}
                  >
                    {status.label}
                  </span>
                </div>

                {/* Name + tagline */}
                <h3 className="text-xl sm:text-2xl font-light text-gray-900 tracking-tight mb-1.5 relative z-10">
                  {p.name}
                </h3>
                <p
                  className="text-xs font-mono uppercase tracking-[0.18em] mb-4"
                  style={{ color: p.accent }}
                >
                  {p.tagline}
                </p>

                <div className="h-px bg-gray-200 mb-4" />

                {/* Description */}
                <p className="text-sm text-gray-600 font-light leading-relaxed mb-5">
                  {p.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {p.features.map((f, idx) => (
                    <li
                      key={idx}
                      className="flex items-start gap-2.5 text-xs text-gray-700 font-light"
                    >
                      <span
                        className="mt-[7px] inline-block w-1 h-1 rounded-full flex-shrink-0"
                        style={{ backgroundColor: p.accent }}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* Footer */}
                <div className="mt-auto flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-400">
                    {p.domain}
                  </span>
                  {isSignup ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSignup(p);
                      }}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-900 text-white hover:bg-black transition-colors"
                      data-testid={`product-${p.code.toLowerCase()}-signup`}
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {p.code === "KAI" ? "Request access" : "Sign up"}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-500 group-hover:text-gray-900 transition-colors">
                      Learn more
                      <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-6 border-t border-gray-200">
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">
            Building AI for the academic frontier · K/AI Product Lab
          </span>
          <a
            href="#contact"
            className="group inline-flex items-center gap-2 text-sm font-light text-gray-700 hover:text-gray-900 border-b border-gray-300 hover:border-gray-900 pb-0.5 transition-colors"
          >
            Request a demo
            <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>

      <WaitlistModal
        open={!!activeConfig}
        onClose={() => setActiveConfig(null)}
        config={activeConfig}
      />
    </section>
  );
};

export default ProductsSection;
